import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, Unlock } from "lucide-react";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function Periods() {
  const { selectedClientId, clients } = useAuth();
  const { t } = useI18n();
  const clientId = selectedClientId;
  const [year, setYear] = useState(new Date().getFullYear());
  const [periods, setPeriods] = useState([]);

  const load = useCallback(async () => {
    if (!clientId) return;
    const { data } = await api.get(`/periods?client_id=${clientId}`);
    setPeriods(data);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const isLocked = (m) => periods.some((p) => p.year === year && p.month === m && p.is_locked);

  const toggle = async (m) => {
    try {
      await api.post("/periods", { client_id: clientId, year, month: m, is_locked: !isLocked(m) });
      toast.success(`${MONTHS[m - 1]} ${year} ${isLocked(m) ? "dibuka" : "dikunci"}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const clientName = clients.find((c) => c.id === clientId)?.name || "";

  return (
    <div className="space-y-4" data-testid="periods-page">
      <div className="page-sticky flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("periods.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("periods.desc")} {clientName && `· ${clientName}`}</p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32" data-testid="period-year"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead>Periode</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {MONTHS.map((m, i) => {
              const locked = isLocked(i + 1);
              return (
                <TableRow key={i} data-testid={`period-row-${i + 1}`}>
                  <TableCell className="text-sm font-medium">{m} {year}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${locked ? "text-rose-600" : "text-emerald-600"}`}>
                      {locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                      {locked ? "Terkunci" : "Terbuka"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant={locked ? "outline" : "default"} size="sm" onClick={() => toggle(i + 1)} data-testid={`toggle-period-${i + 1}`}>
                      {locked ? "Buka" : "Kunci"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
